/**
 * The permissions folder has utilities and components for handling permission related rendering.
 * - `permissions.tsx` file relates to checking if you are allowed to do something
 * - `permission-keys.tsx` file relates to the `resource.action` combinations. Can you do `action` on `resource`?
 * - `permission-messages.tsx` file relates to the `reason` and `messaging` related to permissions.
 *   - What is the internal `reason` why you can't do something and what `messaging` is displayed to the user.
 */

import { Button, ButtonProps, CircularProgress, Tooltip } from "@mui/material";
import { Reason } from "data/models";
import React from "react";
import { AnyPermissionsKey } from "./permission-keys";
import { PermissionMessageTemplateFn } from "./permission-messages";
import { usePermissionProps, usePermissions } from "./usePermissions";

// Clips and Batch have subscription product keys, purchases made on legacy subscriptions are always available (DS-5214)
export const LegacyProductKeys = ["freemium", "professional", "enterprise"];

interface PermissionsWrapperProps {
  keys: AnyPermissionsKey[]; // User must be allowed to complete all of these actions on all resources to proceed
  messages?: Partial<Record<Reason, React.ReactNode>>; // A default set of messages are shown to the user for each reason but these can be overridden.
  children?: React.ReactNode; // The children are rendered if permissionKeys is allowed
  permissionMessageTemplateFn?: PermissionMessageTemplateFn;
  usePermissionProps?: usePermissionProps;
  fallbackToChildren?: Reason[]; // If permission is denied for one of these reasons, render children instead of denial message
  showLoadingState?: boolean; // Whether to show loading state while permissions are being checked
}

export const PermissionsWrapper = (props: PermissionsWrapperProps) => {
  const {
    isPermitted,
    permissionDeniedMessageString,
    _permissionDeniedReason,
    _permissionDeniedMessage,
    _permissionDeniedKey,
    isPermissionsSuccess,
    isPermissionsLoading,
  } = usePermissions({ ...props.usePermissionProps });

  const displayContent = () => {
    // Check if permission is allowed
    if (isPermissionsLoading && props.showLoadingState !== false) {
      return <CircularProgress />;
    } else if (isPermitted(props.keys)) {
      return props.children;
    } else if (
      !isPermitted(props.keys) &&
      _permissionDeniedReason(props.keys) !== undefined &&
      _permissionDeniedKey(props.keys) !== undefined
    ) {
      const deniedReason = _permissionDeniedReason(props.keys);

      // Check if this denial reason should fallback to children
      if (
        props.fallbackToChildren &&
        deniedReason &&
        props.fallbackToChildren.includes(deniedReason)
      ) {
        return props.children;
      }

      return _permissionDeniedMessage(
        props.keys,
        props.messages,
        props.permissionMessageTemplateFn
      );
    } else {
      // Permission is not allowed and there is no reason
      // TODO DS-5166: This is an error state but should not be possible? :shrug:
    }
  };

  return displayContent();
};

// TODO DS-5166: Probably move this somewhere more generic
interface TooltipWrapperProps {
  title: String;
  children: React.ReactNode;
  style?: React.CSSProperties;
}
export const TooltipWrapper = (props: TooltipWrapperProps) => {
  return (
    <Tooltip style={props.style} title={props.title}>
      <span>{props.children}</span>
    </Tooltip>
  );
};

interface PermissionTooltipButtonProps extends Omit<ButtonProps, "disabled"> {
  permissionKeys: AnyPermissionsKey[];
  tooltipMessage?: string;
  usePermissionProps?: usePermissionProps;
  additionalDisabled?: boolean; // Additional disabled condition that doesn't trigger permission tooltip
  isDisabled?: Boolean;
}

export const PermissionTooltipButton = (
  props: PermissionTooltipButtonProps
) => {
  const {
    permissionKeys,
    tooltipMessage,
    usePermissionProps,
    additionalDisabled,
    ...buttonProps
  } = props;

  const { isPermitted, permissionDeniedMessageString, isPermissionsSuccess } =
    usePermissions(usePermissionProps);

  const isPermissionDisabled =
    !isPermissionsSuccess || !isPermitted(permissionKeys);
  const isTotallyDisabled =
    isPermissionDisabled || (additionalDisabled ?? false);

  // Only show tooltip for permission-related disabling
  const tooltipTitle = isPermissionDisabled
    ? tooltipMessage || permissionDeniedMessageString(permissionKeys)
    : "";

  return (
    <Tooltip title={tooltipTitle}>
      <span>
        <Button
          {...buttonProps}
          disabled={isTotallyDisabled || !!props.isDisabled}
        />
      </span>
    </Tooltip>
  );
};

interface PermissionTooltipWrapperProps {
  permissionKeys: AnyPermissionsKey[];
  tooltipMessage?: string;
  usePermissionProps?: usePermissionProps;
  children: React.ReactElement;
  isDisabled?: Boolean;
}

export const PermissionTooltipWrapper = (
  props: PermissionTooltipWrapperProps
) => {
  const { permissionKeys, tooltipMessage, usePermissionProps, children } =
    props;

  const { isPermitted, permissionDeniedMessageString, isPermissionsSuccess } =
    usePermissions(usePermissionProps);

  const isDisabled = !isPermissionsSuccess || !isPermitted(permissionKeys);
  const tooltipTitle = isDisabled
    ? tooltipMessage || permissionDeniedMessageString(permissionKeys)
    : "";

  const childWithDisabled = React.cloneElement(children, {
    disabled: isDisabled || !!props.isDisabled,
  });

  return (
    <Tooltip title={tooltipTitle}>
      <span>{childWithDisabled}</span>
    </Tooltip>
  );
};
